import { useEffect, useId, useRef, useState } from "react";
import { formatText } from "../i18n/texts";
import { SectionMessage, Toast } from "../ui/feedback";
import { ChatIcon } from "../ui/icons/Icon";
import { ContactTagBadge } from "./ContactTagBadge";
import type { ContactDirectoryClient } from "./contactDirectoryClient";
import { ContactRequestFailure } from "./contactDirectoryClient";
import { searchThreshold, type ContactPerson, type TagDefinition } from "./contactModel";
import styles from "./ContactTagSelector.module.css";
import { contains } from "./searchText";
import { TagCatalogClient } from "./tagCatalogClient";
import { colored, styleObject, tagStyle } from "./tagColor";
import { hidePopover, togglePopover } from "./workspaceDom";

// Selector de etiquetas de un contacto (ContactTagSelector.razor, variante moderna, del portal actual): la ficha guarda
// al instante, cada × quita con «Deshacer» y el editor solo cambia su borrador. El servidor decide el permiso; el
// selector solo lo refleja (escribir, administrar el catálogo).

type Props = {
  api: ContactDirectoryClient;
  labels: Record<string, string>;
  tenant: number;
  user: number;
  contactId: number | null;
  value: string[];
  draftOnly?: boolean;
  enableAssign?: boolean;
  addLabel?: string;
  /** El color de cada etiqueta que ya conoce Contactos (todas las páginas del catálogo). */
  colorOf?: (name: string) => string | null | undefined;
  onChanged?: (tags: string[]) => void;
};

const lower = (value: string) => value.toLowerCase();
const has = (list: Iterable<string>, name: string) => [...list].some((item) => lower(item) === lower(name));
const sortNames = (list: string[]) =>
  [...list].sort((a, b) => (lower(a) < lower(b) ? -1 : lower(a) > lower(b) ? 1 : 0));

export function ContactTagSelector(props: Props) {
  const { api, labels, contactId, draftOnly = false, enableAssign = true } = props;
  const s = styles.scope;
  const C = (key: string) => labels["C1_" + key] ?? `[C1_${key}]`;
  const L = (key: string) => labels["E1_" + key] ?? `[E1_${key}]`;
  const titleId = "e1-tag-selector-" + useId().replace(/[^a-z0-9]/gi, "");
  const dialog = useRef<HTMLDivElement>(null);
  const tags = useRef(new TagCatalogClient(props.tenant, props.user));
  const person = useRef<ContactPerson | null>(null);
  const openedSession = useRef<string | null>(null);
  const searchPending = useRef<AbortController | null>(null);
  const sequence = useRef(0);
  const lifetime = useRef(new AbortController());
  const [currentTags, setCurrentTags] = useState<string[]>(props.value);
  const [seenValue, setSeenValue] = useState(props.value);
  if (seenValue !== props.value) {
    setSeenValue(props.value);
    setCurrentTags([...props.value]);
  }
  const [original, setOriginal] = useState<string[]>([]);
  const [draft, setDraft] = useState<string[]>([]);
  const [options, setOptions] = useState<TagDefinition[]>([]);
  const [searchBase, setSearchBase] = useState<TagDefinition[]>([]);
  const [searchBaseLoaded, setSearchBaseLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [more, setMore] = useState(false);
  const [undoTags, setUndoTags] = useState<string[] | null>(null);
  const [lastRemoved, setLastRemoved] = useState<string | null>(null);
  const [receipt, setReceipt] = useState(0);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [errorReceipt, setErrorReceipt] = useState(0);
  const [catalogColors, setCatalogColors] = useState<Record<string, string | null>>({});
  const work = useRef({ saving, removing });
  useEffect(() => {
    work.current = { saving, removing };
  });

  const color = (tag: string) =>
    props.colorOf?.(tag) ??
    options.find((option) => lower(option.name) === lower(tag))?.color ??
    catalogColors[lower(tag)] ??
    null;
  const message = (exception: unknown) => {
    const code = exception instanceof ContactRequestFailure ? exception.code : "";
    const map: Record<string, string> = {
      STALE_VERSION: "STALE",
      CRM_FORBIDDEN: "DENIED",
      SESSION_INVALID: "DENIED",
      ACCESS_REVOKED: "DENIED",
      TENANT_CHANGED: "DENIED",
      TAG_ARCHIVED: "TAG_ARCHIVED",
      TAG_NOT_FOUND: "TAG_NOT_FOUND",
    };
    return L(map[code] ?? "UNAVAILABLE");
  };

  // El contexto que ya confirmó Contactos se reutiliza; solo se pide si no hay uno reciente.
  useEffect(() => {
    if (lifetime.current.signal.aborted) lifetime.current = new AbortController();
    const signal = lifetime.current.signal;
    api.ensureRecentContext(signal).then(
      () => {
        setCanWrite(api.currentRights?.canWrite === true);
        setCanManage(api.currentRights?.canManageTags === true && api.currentRights.canOpenChat);
      },
      (exception: unknown) => {
        if (exception instanceof ContactRequestFailure) setCanWrite(false);
        else if (!signal.aborted) setError(L("UNAVAILABLE"));
      },
    );
    const controller = lifetime.current;
    return () => {
      controller.abort();
      searchPending.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fuera de Contactos (sin colores conocidos) se leen del catálogo las etiquetas que faltan.
  const requested = useRef(new Set<string>());
  useEffect(() => {
    if (props.colorOf || api.currentRights?.canReadTags !== true) return;
    const missing = currentTags.filter((tag) => !requested.current.has(lower(tag)));
    if (missing.length === 0) return;
    missing.forEach((tag) => requested.current.add(lower(tag)));
    tags.current
      .list({
        search: null,
        state: "ALL",
        from: null,
        to: null,
        sort: "CREATED",
        page: 1,
        pageSize: 100,
        selectedIds: null,
        selectedNames: missing,
      })
      .then(
        (page) =>
          setCatalogColors((previous) => {
            const next = { ...previous };
            for (const row of page.items) next[lower(row.tag.name)] = row.tag.color;
            return next;
          }),
        () => undefined,
      );
  });

  const runSearch = async (text: string, base: TagDefinition[], loaded: boolean) => {
    searchPending.current?.abort();
    const controller = new AbortController();
    searchPending.current = controller;
    const current = ++sequence.current;
    setLoading(true);
    if (loaded) {
      setOptions(base);
      if (text.length < 2) {
        setLoading(false);
        return;
      }
    }
    if (text.length === 1) {
      setLoading(false);
      return;
    }
    try {
      if (text.length > 0)
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(resolve, 250);
          controller.signal.addEventListener("abort", () => {
            clearTimeout(timer);
            reject(new DOMException("cancelado", "AbortError"));
          });
        });
      const page = await tags.current.list({
        search: text,
        state: "ALL",
        from: null,
        to: null,
        sort: "NAME",
        page: 1,
        pageSize: 100,
        selectedIds: null,
        selectedNames: null,
      });
      if (current !== sequence.current) return;
      const found = page.items.map((row) => row.tag);
      setOptions(found);
      setMore(page.total > page.items.length);
      if (text.length === 0) {
        setSearchBase(found);
        setSearchBaseLoaded(true);
      }
      setCanManage(page.access.canManageTags === true && page.access.canOpenChat !== false);
    } catch (exception) {
      if (controller.signal.aborted) return;
      setPanelError(message(exception));
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  };

  const open = async () => {
    setError(null);
    setPanelError(null);
    try {
      await api.ensureContext(lifetime.current.signal);
      if (api.currentRights?.canWrite !== true) throw new ContactRequestFailure(403, "CRM_FORBIDDEN");
      if (contactId !== null) person.current = (await api.get(contactId, lifetime.current.signal)).contact;
      let first = [...(draftOnly ? props.value : (person.current?.tags ?? props.value))];
      // Todas las asignaciones, aunque estén más allá de la primera página del catálogo.
      if (first.length > 0) {
        const assigned = await tags.current.list({
          search: null,
          state: "ALL",
          from: null,
          to: null,
          sort: "CREATED",
          page: 1,
          pageSize: 100,
          selectedIds: null,
          selectedNames: first,
        });
        first = assigned.items.map((row) => row.tag.name);
      }
      setOriginal(first);
      setDraft(first);
      setSearch("");
      setSearchBase([]);
      setSearchBaseLoaded(false);
      openedSession.current = api.currentSessionStamp;
      togglePopover(dialog.current);
      await runSearch("", [], false);
    } catch (exception) {
      if (
        exception instanceof ContactRequestFailure &&
        ["CRM_FORBIDDEN", "ACCESS_REVOKED", "SESSION_INVALID", "TENANT_CHANGED"].includes(exception.code)
      )
        setCanWrite(false);
      setError(message(exception));
    }
  };

  /** Guarda la selección; devuelve el error del panel (o null). */
  const persist = async (values: string[], close: boolean, undoing: boolean): Promise<string | null> => {
    if (work.current.saving) return null;
    setSaving(true);
    work.current.saving = true;
    setPanelError(null);
    try {
      await api.ensureContext(lifetime.current.signal);
      if (openedSession.current !== api.currentSessionStamp || api.currentRights?.canWrite !== true) {
        setDraft([]);
        setOriginal([]);
        setOptions([]);
        throw new ContactRequestFailure(403, "ACCESS_REVOKED");
      }
      const sorted = sortNames(values);
      if (!draftOnly && person.current) {
        const before = [...currentTags];
        const target = person.current;
        const saved = await api.save(
          target.id,
          {
            firstName: target.firstName,
            lastName: target.lastName,
            expectedVersion: target.rowVersion,
            commercial: {
              email: target.email,
              phone: target.phone,
              relationshipStatus: target.relationshipStatus,
              tags: sorted,
            },
          },
          crypto.randomUUID().replace(/-/g, ""),
          lifetime.current.signal,
        );
        person.current = saved.contact;
        if (!undoing) {
          setUndoTags(before);
          setReceipt((count) => count + 1);
        }
      }
      setCurrentTags(sorted);
      props.onChanged?.(sorted);
      if (close) hidePopover(dialog.current);
      return null;
    } catch (exception) {
      const text = message(exception);
      setPanelError(text);
      return text;
    } finally {
      setSaving(false);
      work.current.saving = false;
    }
  };

  /** KB1: si quitar falla, la etiqueta queda como estaba y el error sale en un aviso. */
  const removeFailed = (text: string) => {
    setDraft([...currentTags]);
    setPanelError(null);
    setRemoveError(text);
    setErrorReceipt((count) => count + 1);
    setUndoTags(null);
    setLastRemoved(null);
  };
  const removeTag = async (name: string) => {
    if (work.current.removing !== null || work.current.saving) return;
    setRemoving(name);
    work.current.removing = name;
    try {
      await api.ensureContext(lifetime.current.signal);
      if (contactId !== null && (person.current === null || !has(currentTags, name)))
        person.current = (await api.get(contactId, lifetime.current.signal)).contact;
      const first = [...(draftOnly ? props.value : (person.current?.tags ?? props.value))];
      setOriginal(first);
      if (!has(first, name)) return;
      const next = first.filter((item) => lower(item) !== lower(name));
      setDraft(next);
      openedSession.current = api.currentSessionStamp;
      const failed = await persist(next, false, false);
      if (failed === null) {
        setLastRemoved(name);
        setRemoveError(null);
      } else removeFailed(failed);
    } catch (exception) {
      removeFailed(message(exception));
    } finally {
      setRemoving(null);
      work.current.removing = null;
    }
  };
  const chooseImmediate = async (name: string) => {
    setLastRemoved(null);
    const previous = [...draft];
    const next = has(draft, name) ? draft.filter((item) => lower(item) !== lower(name)) : [...draft, name];
    setDraft(next);
    const failed = await persist(next, false, false);
    if (failed !== null) setDraft(previous);
  };
  const undo = async () => {
    if (!undoTags) return;
    setDraft([...undoTags]);
    const failed = await persist(undoTags, false, true);
    if (failed === null) {
      setUndoTags(null);
      setLastRemoved(null);
    } else removeFailed(failed);
  };
  const clearSearch = async () => {
    setSearch("");
    const pending = runSearch("", searchBase, searchBaseLoaded);
    document.getElementById(titleId + "-search")?.focus();
    await pending;
  };

  const optionCount = searchBase.filter((tag) => !tag.archived || has(original, tag.name)).length;
  const showSearch = search.length > 0 || more || optionCount > searchThreshold;
  const assign = canWrite && enableAssign;
  return (
    <div className={`e1-tag-assignment c1-inline-tag-selector ${s}`}>
      {currentTags.length > 0 ? (
        <div className={`c1-tags ${s}`}>
          {currentTags.map((tag) => (
            <span
              key={tag}
              className={`contact-tag-wrap ${colored(color(tag)) ? "contact-tag-colored" : ""} ${s}`}
              style={styleObject(tagStyle(color(tag)))}
            >
              <ContactTagBadge name={tag} color={color(tag)} />
              {assign ? (
                <button
                  type="button"
                  className={`c1-tag-remove ${s}`}
                  disabled={saving || removing !== null}
                  title={formatText(C("REMOVE_TAG"), tag)}
                  aria-label={formatText(C("REMOVE_TAG"), tag)}
                  onClick={() => void removeTag(tag)}
                >
                  <ChatIcon name="x" />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
      {assign ? (
        <button
          id={titleId + "-trigger"}
          type="button"
          className={`button secondary ${s}`}
          aria-haspopup="menu"
          aria-label={props.addLabel ?? C("ADD_TAG")}
          onClick={() => void open()}
        >
          <ChatIcon name="plus" />
          {props.addLabel ?? C("ADD_TAG")}
        </button>
      ) : null}
      {error !== null ? <SectionMessage message={error} tone="error" /> : null}
      {/* El espacio que Blazor deja entre el aviso y el menú. */}
      {"\n    "}
      <div
        id={titleId}
        popover="auto"
        className={`c1-popover-panel c1-tag-menu c1-sheet ${s}`}
        ref={dialog}
        role="menu"
        aria-label={L("ASSIGN")}
        data-contact-popover=""
        data-contact-popover-trigger={titleId + "-trigger"}
      >
        {/* C5-03 · C30: en el celular es una ventana a pantalla completa con ← y el título; en escritorio, el menú. */}
        <div className={`c1-sheet-head ${s}`}>
          <button
            type="button"
            className={`c1-icon icon-button ${s}`}
            popoverTarget={titleId}
            popoverTargetAction="hide"
            aria-label={C("BACK")}
          >
            <ChatIcon name="arrow-left" />
          </button>
          <h2 className={s}>{L("ASSIGN")}</h2>
        </div>
        {showSearch ? (
          <div className={`c1-search ${s}`}>
            <ChatIcon name="search" />
            <input
              className={s}
              type="search"
              id={titleId + "-search"}
              enterKeyHint="search"
              aria-label={L("ASSIGN_SEARCH")}
              placeholder={L("ASSIGN_SEARCH")}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                void runSearch(event.target.value, searchBase, searchBaseLoaded);
              }}
            />
            {search.length > 0 ? (
              <button
                type="button"
                className={`c1-icon icon-button ${s}`}
                aria-label={C("CLEAR_SEARCH")}
                onClick={() => void clearSearch()}
              >
                <ChatIcon name="x" />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className={`c1-tag-options ${s}`} aria-busy={loading ? "true" : "false"}>
          {options
            .filter((tag) => (!tag.archived || has(original, tag.name)) && contains(tag.name, search))
            .map((tag) => {
              const checked = has(draft, tag.name);
              return (
                <button
                  key={tag.id}
                  className={s}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={checked ? "true" : "false"}
                  disabled={saving || (!checked && (draft.length >= 20 || tag.archived))}
                  onClick={() => void chooseImmediate(tag.name)}
                >
                  <ContactTagBadge name={tag.name} color={color(tag.name)} />
                  {checked ? <ChatIcon name="check" /> : null}
                </button>
              );
            })}
        </div>
        {more ? <p className={s}>{L("MORE")}</p> : null}
        {draft.length >= 20 ? <p className={s}>{L("TAG_LIMIT")}</p> : null}
        {panelError !== null ? <SectionMessage message={panelError} tone="error" /> : null}
        {canManage ? (
          <a className={`c1-tag-manage ${s}`} href="/portal/chat/configuracion/etiquetas">
            {L("MANAGE")}
          </a>
        ) : null}
        <p className={`trace ${s}`}>{L("ASSIGN_HELP_BASE")}</p>
      </div>
      {removeError !== null ? <Toast key={"e" + errorReceipt} tone="error" message={removeError} /> : null}
      {undoTags !== null ? (
        <Toast
          key={"r" + receipt}
          message={lastRemoved === null ? C("TAGS_UPDATED") : formatText(C("TAG_REMOVED"), lastRemoved)}
          action={
            <button
              type="button"
              className={`c1-toast-undo ${s}`}
              disabled={saving || removing !== null}
              onClick={() => void undo()}
            >
              {C("UNDO")}
            </button>
          }
        />
      ) : null}
    </div>
  );
}
